import { type ClientId, type ClientState } from './bridge-api-client-types';

/* eslint-disable no-console */

/**
 * Event handler for when a new client is added to the current bridge API instance.
 */
export type ClientAddedEventHandler = (clientId: ClientId) => void;

/**
 * Maintains a registry of all local and remote clients known to the current bridge API instance.
 *
 * Used to determine and mark the reachability of clients from other clients - e.g., if `rovo-chat`
 * can be reached from inside of a cross-origin `iframe` in a browser context.
 */
export class ClientRegistry {
	/**
	 * A registry of all clients known to the current bridge API instance.
	 *
	 * @description NOTE: Clients undergo state transitions from pending -> ready until the current bridge
	 * API instance is able to communicate with them.
	 */
	private registry = new Map<string, ClientState>();

	/**
	 * Event handlers for when clients are added to the registry.
	 */
	private onClientAddedHandlers = new Set<ClientAddedEventHandler>();

	/**
	 * Yields the current state of a client.
	 * @param clientId
	 * @returns current state
	 */
	public get(clientId: ClientId): ClientState | undefined {
		return this.registry.get(clientId);
	}

	/**
	 * Yields whether a given client is known or not to the registry.
	 * @param clientId unique identifier for the client
	 */
	public has(clientId: ClientId): boolean {
		return this.registry.has(clientId);
	}

	/**
	 * Adds an entry for a new, pending client. It will be marked as `ready` once a successful handshake transpires.
	 *
	 * @description Will early exit with a warning if the client being marked as `pending` is already `ready`. This
	 * is an anomalous state transition which we do not expect.
	 *
	 * @param clientId unique identifier for the client
	 * @returns void
	 */
	public addPendingClient(clientId: ClientId): void {
		const client = this.get(clientId);
		if (client && client.state === 'ready') {
			console.warn('tried to mark', clientId, 'as `pending` when already in `ready` state');
			return;
		}

		this.registry.set(clientId, {
			id: clientId,
			state: 'pending',
		});
	}

	/**
	 * Adds an entry for a local client. It will be immediately ready for direct communication in the same JS context.
	 *
	 * @description Will early exit with a warning if the client being marked as `local` if already `remote`. This
	 * is an anomalous state transition which we do not expect.
	 *
	 * @param clientId unique identifier for the client
	 * @returns void
	 */
	public addLocalClient(clientId: ClientId): void {
		if (this.isRemoteClient(clientId)) {
			console.warn('tried to mark', clientId, 'as `local` when already tracked as `remote`');
			return;
		}

		const wasAlreadyPresent = this.has(clientId);

		this.registry.set(clientId, {
			id: clientId,
			type: 'local',
			state: 'ready',
		});

		// only emit the event if this is a new client
		if (!wasAlreadyPresent) {
			this.emitClientAdded(clientId);
		}
	}

	/**
	 * Adds an entry for a remote client. It will be ready for asynchronous communication once a handshake transpires.
	 *
	 * @description Will early exit with a warning if the client being marked as `remote` if already `local`. This
	 * is an anomalous state transition which we do not expect.
	 *
	 * @param clientId unique identifier for the client
	 * @returns void
	 */
	public addRemoteClient(clientId: ClientId): void {
		if (this.isLocalClient(clientId)) {
			console.warn('tried to mark', clientId, 'as `remote` when already tracked as `local`');
			return;
		}

		const wasAlreadyPresent = this.has(clientId);

		this.registry.set(clientId, {
			id: clientId,
			type: 'remote',
			state: 'ready',
		});

		// only emit the event if this is a new client
		if (!wasAlreadyPresent) {
			this.emitClientAdded(clientId);
		}
	}

	public isPendingClient(clientId: ClientId): boolean {
		const client = this.get(clientId);
		return client?.state === 'pending';
	}

	public isLocalClient(clientId: ClientId): boolean {
		const client = this.get(clientId);
		return client?.state === 'ready' && client.type === 'local';
	}

	public isRemoteClient(clientId: ClientId): boolean {
		const client = this.get(clientId);
		return client?.state === 'ready' && client.type === 'remote';
	}

	public get size(): number {
		return this.registry.size;
	}

	/**
	 * Removes a client from the registry.
	 *
	 * @param clientId unique identifier for the client to remove
	 * @returns true if the client was removed, false if it wasn't found
	 */
	public removeClient(clientId: ClientId): boolean {
		return this.registry.delete(clientId);
	}

	/**
	 * Gets all client IDs currently in the registry.
	 *
	 * @returns array of all client IDs
	 */
	public getAllClientIds(): ClientId[] {
		return Array.from(this.registry.keys()) as ClientId[];
	}

	/**
	 * Register a handler to be called when a client is added to the registry.
	 *
	 * @param handler Function to call when a client is added
	 */
	public onClientAdded(handler: ClientAddedEventHandler): void {
		this.onClientAddedHandlers.add(handler);
	}

	/**
	 * Emit client added event to all registered handlers.
	 *
	 * @param clientId The client ID that was added
	 */
	private emitClientAdded(clientId: ClientId): void {
		for (const handler of this.onClientAddedHandlers) {
			try {
				handler(clientId);
			} catch (error) {
				console.error('Error in client added handler:', error);
			}
		}
	}

	/**
	 * Clean up all event handlers.
	 */
	public destroy(): void {
		this.onClientAddedHandlers.clear();
		this.registry.clear();
	}
}
